import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ApiError,
  api,
  can,
  normalizeScore,
  refusalReason,
  shortDate,
  tierLabel,
  type ModelExplain,
  type ModelPromoteResult,
  type ModelValidation,
  type ModelVersion,
  type RiskModelDetail,
  type ScoringModel,
  type Vendor,
} from '../lib/api'
import { ErrorBox, Loading } from '../components/DataTable'
import { BandChip } from '../components/BandChip'
import { EMPTY_MODEL, RiskModelEditor } from '../components/RiskModelEditor'
import { Status, Toggletip, type Tone } from '../components/kit'
import { ReasonTip } from '../components/vendor/ReasonTip'
import { VendorChoice } from '../components/VendorChoice'
import '../styles/studio.css'
import { Crumbs } from '../components/Crumbs'
import '../components/kit/back.css'
import '../styles/sweep.css'

// A version's state as a label. Retired is finished, so it goes quiet; a code
// the console does not know still shows, in words rather than capitals.
const VERSION_STATUS: Record<string, { tone: Tone; label: string }> = {
  DRAFT: { tone: 'info', label: 'Draft' },
  ACTIVE: { tone: 'ok', label: 'Active' },
  RETIRED: { tone: 'plain', label: 'Retired' },
}

function VersionStatus({ status }: { status: string }) {
  const known = VERSION_STATUS[status]
  if (known) return <Status tone={known.tone}>{known.label}</Status>
  const words = status.toLowerCase().replace(/_/g, ' ')
  return <Status tone="neutral">{words.charAt(0).toUpperCase() + words.slice(1)}</Status>
}

function asApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))
}

export function RiskModelStudio({ id }: { id: string }) {
  const [detail, setDetail] = useState<RiskModelDetail | null>(null)
  const [error, setError] = useState<unknown>(null)

  const [openId, setOpenId] = useState<string | null>(null)
  const [version, setVersion] = useState<ModelVersion | null>(null)
  const [draft, setDraft] = useState<ScoringModel | null>(null)
  const [dirty, setDirty] = useState(false)

  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<ApiError | null>(null)
  const [savedNote, setSavedNote] = useState<string | null>(null)

  const [drafting, setDrafting] = useState(false)
  const [draftError, setDraftError] = useState<ApiError | null>(null)

  const [validation, setValidation] = useState<ModelValidation | null>(null)
  const [validating, setValidating] = useState(false)
  const [validateError, setValidateError] = useState<ApiError | null>(null)

  const [vendors, setVendors] = useState<Vendor[]>([])
  const [explainVendor, setExplainVendor] = useState('')
  const [explain, setExplain] = useState<ModelExplain | null>(null)
  const [explainError, setExplainError] = useState<ApiError | null>(null)

  const [recompute, setRecompute] = useState(false)
  const [promoting, setPromoting] = useState(false)
  const [promoteError, setPromoteError] = useState<ApiError | null>(null)
  const [promoted, setPromoted] = useState<ModelPromoteResult | null>(null)

  const load = useCallback(() => {
    api
      .riskModel(id)
      .then((d) => {
        setDetail(d)
        setError(null)
        setOpenId((prev) => prev ?? d.riskModel.activeVersionId ?? d.versions[0]?.id ?? null)
      })
      .catch(setError)
  }, [id])

  useEffect(load, [load])

  useEffect(() => {
    api
      .vendors({})
      .then((r) => setVendors(r.vendors))
      .catch(() => setVendors([]))
  }, [])

  useEffect(() => {
    if (!openId) {
      setVersion(null)
      setDraft(null)
      return
    }
    let live = true
    setVersion(null)
    setDraft(null)
    setValidation(null)
    setExplain(null)
    setSavedNote(null)
    setDirty(false)
    api
      .riskModelVersion(openId)
      .then((v) => {
        if (!live) return
        setVersion(v)
        setDraft(v.model ?? EMPTY_MODEL)
      })
      .catch((err) => {
        if (live) setError(err)
      })
    return () => {
      live = false
    }
  }, [openId])

  // Validate writes nothing, so it can run on demand without a confirmation.
  // It is also the only source of the key universe the editor highlights
  // against, which is why the editor is quieter until it has run once.
  const validate = useCallback(() => {
    if (!openId) return
    setValidating(true)
    setValidateError(null)
    api
      .validateRiskModelVersion(openId)
      .then(setValidation)
      .catch((err) => setValidateError(asApiError(err)))
      .finally(() => setValidating(false))
  }, [openId])

  useEffect(() => {
    if (openId) validate()
  }, [openId, validate])

  function save() {
    if (!openId || !draft) return
    setSaving(true)
    setSaveError(null)
    api
      .saveRiskModelVersion(openId, draft)
      .then((r) => {
        setSavedNote(r.note)
        setDirty(false)
        validate()
      })
      .catch((err) => setSaveError(asApiError(err)))
      .finally(() => setSaving(false))
  }

  function newDraft(fromVersionId?: string) {
    setDrafting(true)
    setDraftError(null)
    setPromoted(null)
    api
      .createRiskModelVersion(id, { fromVersionId })
      .then((r) => {
        setOpenId(r.id)
        load()
      })
      .catch((err) => setDraftError(asApiError(err)))
      .finally(() => setDrafting(false))
  }

  function runExplain() {
    if (!openId || !explainVendor) return
    setExplain(null)
    setExplainError(null)
    api
      .explainRiskModelVersion(openId, { vendorId: explainVendor })
      .then(setExplain)
      .catch((err) => setExplainError(asApiError(err)))
  }

  function promote() {
    if (!openId) return
    setPromoting(true)
    setPromoteError(null)
    api
      .promoteRiskModelVersion(openId, { recompute })
      .then((r) => {
        setPromoted(r)
        load()
        return api.riskModelVersion(openId).then(setVersion)
      })
      .catch((err) => setPromoteError(asApiError(err)))
      .finally(() => setPromoting(false))
  }

  // What every question in this tenant's published questionnaires asks, by
  // key. Read once for the whole studio rather than per weight row: a model
  // carries eighty-seven of them and a request each would be eighty-seven
  // requests to render one panel.
  const [prompts, setPrompts] = useState<Record<string, string>>({})
  useEffect(() => {
    let live = true
    api
      .questionPrompts()
      .then((r) => {
        if (live) setPrompts(r.prompts)
      })
      .catch(() => {
        // A studio that cannot read the prompts still edits weights. The row
        // falls back to the key, which is what it showed before.
      })
    return () => {
      live = false
    }
  }, [])

  const knownKeys = useMemo(() => {
    // The validate response names the questionnaire versions this model is
    // checked against but not their keys, so the unweighted list plus the
    // model's own weights is the closest thing to the universe the editor can
    // highlight against without a second round trip per keystroke.
    const keys = new Set<string>(validation?.questionsCarryingNoWeight ?? [])
    if (validation) for (const k of Object.keys(draft?.weights ?? {})) keys.add(k)
    return [...keys].sort()
  }, [validation, draft])

  if (error) return <ErrorBox error={error} what="This risk model" />
  if (!detail) return <Loading what="the risk model" />

  const m = detail.riskModel
  const editable = version?.status === 'DRAFT'

  return (
    <>
      <div className="page-head">
        <div>
          <Crumbs className="kit-crumbs" home={{ label: 'Risk models', href: '#/risk-models' }} current={m.name} />
          <h1>
            <span className="mono">{m.code}</span> {m.name}
          </h1>
          {/* The tenant's own description stands as written. Without one, the
              console's summary of how a model scores is a line and an (i). */}
          {m.description ? (
            <p className="muted">{m.description}</p>
          ) : (
            <p className="muted">
              Tier rules, weights and gates turn answers into a band{' '}
              <Toggletip label="How a risk model scores">
                Tier rules pick a row of the band matrix, weights turn answers into a control score, and gates can only
                ever make a band worse.
              </Toggletip>
            </p>
          )}
        </div>
        <div className="studio-header-actions">
          <button
            type="button"
            className="primary"
            disabled={drafting || !can('riskmodel.author')}
            title={can('riskmodel.author') ? undefined : refusalReason('riskmodel.author')}
            onClick={() => newDraft(m.activeVersionId ?? version?.id)}
            data-action="tprm.risk-model.a03"
          >
            {drafting ? 'Creating' : m.versionCount === 0 ? 'Draft the first version' : 'Draft the next version'}
          </button>
        </div>
      </div>

      {draftError && <ErrorBox error={draftError} what="The draft" />}

      {detail.usedAgainst.length > 0 && (
        <div className="panel panel-flush">
          <h2>Questionnaires this model scores</h2>
          <ul className="rule-list">
            {detail.usedAgainst.map((s) => (
              <li key={s.questionnaireVersionId}>
                <span className="mono">{s.questionnaireCode}</span> version {s.versionNo} · {s.questionCount} questions
                <span className="muted"> · {s.because}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="panel">
        <h2>Versions</h2>
        {detail.versions.length === 0 ? (
          <div className="empty">
            <strong>No versions yet</strong>
            This model scores nothing until a version is drafted and promoted
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <caption className="vh">Versions of {m.code}</caption>
              <thead>
                <tr>
                  <th scope="col">Version</th>
                  <th scope="col">Status</th>
                  <th scope="col" className="num">
                    Floor
                  </th>
                  <th scope="col">Ceiling</th>
                  <th scope="col">Content hash</th>
                  <th scope="col">Activated by</th>
                  <th scope="col">
                    <span className="vh">Open</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {detail.versions.map((v) => (
                  <tr key={v.id} aria-current={v.id === openId ? 'true' : undefined}>
                    <th scope="row">v{v.versionNo}</th>
                    <td>
                      <VersionStatus status={v.status} />
                    </td>
                    <td className="num">{v.coverageFloor}%</td>
                    <td className="mono">{v.provisionalCeiling}</td>
                    <td>
                      {v.contentSha256 ? (
                        <span className="mono" title={v.contentSha256}>
                          {v.contentSha256.slice(0, 12)}
                        </span>
                      ) : (
                        <span className="muted">not stamped until active</span>
                      )}
                    </td>
                    <td>
                      {v.activatedBy ? (
                        <>
                          {v.activatedBy}
                          <span className="cell-sub">{v.activatedAt ? shortDate(v.activatedAt) : ''}</span>
                        </>
                      ) : (
                        <span className="muted">never</span>
                      )}
                    </td>
                    <td>
                      <button type="button" onClick={() => setOpenId(v.id)}>
                        {v.id === openId ? 'Open' : 'View'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {openId && !version && <Loading what="the version" />}

      {version && draft && (
        <>
          <div className="studio-bar" role="region" aria-label="Version actions">
            <span className="sw-row">
              Version {version.versionNo} <VersionStatus status={version.status} />
            </span>
            {editable ? (
              dirty ? (
                <Status tone="warn">Unsaved</Status>
              ) : (
                <Status tone="plain">All saved</Status>
              )
            ) : (
              <>
                <Status tone="plain">Frozen</Status>
                <span className="muted">Draft the next version to change anything</span>
              </>
            )}
            <span className="studio-spacer" />
            <button type="button" onClick={validate} disabled={validating} data-action="tprm.risk-model.a01">
              {validating ? 'Checking' : 'Check this model'}
            </button>
            {editable && (
              <button
                type="button"
                className="primary"
                onClick={save}
                disabled={saving || !can('riskmodel.author')}
                title={can('riskmodel.author') ? undefined : refusalReason('riskmodel.author')}
                data-action="tprm.risk-model.a02"
              >
                {saving ? 'Saving' : 'Save draft'}
              </button>
            )}
          </div>

          {saveError && <ErrorBox error={saveError} what="The draft" />}
          {/* The API's note says what a save does and does not do. It is kept
              word for word behind the (i) beside the confirmation. */}
          {savedNote && (
            <div className="ok-note sw-row" role="status">
              <strong>Saved</strong>
              <ReasonTip label="What saving did" text={savedNote} />
            </div>
          )}

          {validateError && <ErrorBox error={validateError} what="The check" />}
          {validation && <ValidationPanel v={validation} />}

          {editable && validation?.valid && (
            <PromotePanel
              recompute={recompute}
              setRecompute={setRecompute}
              promoting={promoting}
              onPromote={promote}
              dirty={dirty}
            />
          )}
          {promoteError && <ErrorBox error={promoteError} what="The promotion" />}
          {promoted && <PromotedPanel result={promoted} />}

          <section className="panel">
            <h2>Try this model against a vendor</h2>
            <div className="pickrow">
              <div className="field studio-grow">
                <label htmlFor="explain-vendor">Vendor</label>
                <VendorChoice bare browse id="explain-vendor" label="Vendor" value={explainVendor} options={vendors} onChange={setExplainVendor} />
              </div>
              <button
                type="button"
                onClick={runExplain}
                disabled={!explainVendor}
                data-action="tprm.risk-model.a04"
              >
                Run it
              </button>
            </div>
            {explainError && <ErrorBox error={explainError} what="The dry run" />}
            {explain && <ExplainPanel e={explain} />}
          </section>

          <RiskModelEditor
            key={version.id}
            model={draft}
            editable={!!editable}
            knownKeys={knownKeys}
            prompts={prompts}
            unweighted={validation?.questionsCarryingNoWeight ?? []}
            onChange={(next) => {
              setDraft(next)
              setDirty(true)
              setSavedNote(null)
            }}
          />
        </>
      )}
    </>
  )
}

function ValidationPanel({ v }: { v: ModelValidation }) {
  return (
    <div className={v.valid ? 'ok-note' : 'issue-list'} role={v.valid ? 'status' : 'alert'}>
      <h3>
        {v.valid
          ? 'This model would be accepted'
          : v.problems.length + (v.problems.length === 1 ? ' problem' : ' problems') + ' would stop this being promoted'}
      </h3>
      {v.problems.length > 0 && (
        <ul>
          {v.problems.map((p, i) => (
            <li key={i}>{p}</li>
          ))}
        </ul>
      )}
      {/* What the check read, numbers first, with the API's note on what a
          check writes kept word for word behind the (i). */}
      <p className="muted sw-row">
        {v.validatedAgainst.length > 0 ? (
          <span>
            Checked against {v.validatedAgainst.map((s) => s.questionnaireCode + ' v' + s.versionNo).join(', ')},{' '}
            {v.questionsCarryingNoWeight.length} question
            {v.questionsCarryingNoWeight.length === 1 ? '' : 's'} carry no weight
          </span>
        ) : (
          <span>Checked against no questionnaire version</span>
        )}
        <ReasonTip label="What the check did" text={v.note} />
      </p>
    </div>
  )
}

function PromotePanel({
  recompute,
  setRecompute,
  promoting,
  onPromote,
  dirty,
}: {
  recompute: boolean
  setRecompute: (v: boolean) => void
  promoting: boolean
  onPromote: () => void
  dirty: boolean
}) {
  return (
    <section className="panel">
      <h2>Promote</h2>
      <fieldset className="fieldset">
        <legend>What happens to scores that already exist</legend>
        <label className="pickrow">
          <input type="radio" name="recompute" checked={!recompute} onChange={() => setRecompute(false)} />
          <span>
            <strong>Apply to new assessments only</strong>
          </span>
        </label>
        <label className="pickrow">
          <input type="radio" name="recompute" checked={recompute} onChange={() => setRecompute(true)} />
          <span>
            <strong>Rescore the portfolio now</strong>
          </span>
        </label>
      </fieldset>
      <div className="commit-bar">
        <button
          type="button"
          className="primary"
          onClick={onPromote}
          disabled={promoting || dirty || !can('riskmodel.promote')}
          title={can('riskmodel.promote') ? undefined : refusalReason('riskmodel.promote')}
          data-action="tprm.risk-model.a05"
        >
          {promoting ? 'Promoting' : recompute ? 'Promote and rescore' : 'Promote'}
        </button>
        {!can('riskmodel.promote') && (
          <span className="muted sw-row">
            <Status tone="neutral">Read only</Status>
            <ReasonTip label="Why you cannot promote" text={refusalReason('riskmodel.promote')} />
          </span>
        )}
      </div>
    </section>
  )
}

function PromotedPanel({ result }: { result: ModelPromoteResult }) {
  const r = result.recompute
  return (
    <div className="ok-note" role="status">
      {/* The counts lead, and the lists below name the vendors. The API's note
          says what the counts mean and what to do next, word for word, one
          press away. */}
      <p className="sw-row">
        <strong>
          Version {result.versionNo} is active, hash {result.contentSha256.slice(0, 12)}
        </strong>
        {r.requested ? (
          <span>
            {r.assessmentsRescored ?? 0} rescored, {r.vendorsChangedBand ?? 0} changed band
          </span>
        ) : (
          r.assessmentsOnPriorVersion !== undefined && (
            <span>
              {r.assessmentsOnPriorVersion} current score{r.assessmentsOnPriorVersion === 1 ? '' : 's'} not rescored
            </span>
          )
        )}
        <ReasonTip label="What promotion changed" text={result.note} />
      </p>

      {r.changes && r.changes.length > 0 && (
        <>
          <h4>Vendors that changed band</h4>
          <ul className="rule-list">
            {r.changes.map((c) => (
              <li key={c.vendorId}>
                <span className="mono">{c.vendorCode}</span> {c.vendorName} <BandChip band={c.from} /> to{' '}
                <BandChip band={c.to} />
              </li>
            ))}
          </ul>
        </>
      )}

      {r.overridesLapsed && r.overridesLapsed.length > 0 && (
        <>
          <h4>Overrides that no longer apply</h4>
          <ul className="rule-list">
            {r.overridesLapsed.map((o) => (
              <li key={o.assessmentReference}>
                <span className="mono">{o.vendorCode}</span> {o.vendorName}: was held at{' '}
                <BandChip band={o.wasOverriddenTo} />
                {o.wasToRunUntil ? ' until ' + shortDate(o.wasToRunUntil) : ''}, now computes{' '}
                <BandChip band={o.computedBandNow} />
              </li>
            ))}
          </ul>
        </>
      )}

      {r.failed && r.failed.length > 0 && (
        <>
          <h4>Assessments that would not rescore</h4>
          <ul className="rule-list">
            {r.failed.map((f) => (
              <li key={f.vendorId}>
                <span className="mono">{f.vendorCode}</span> {f.error}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

function ExplainPanel({ e }: { e: ModelExplain }) {
  const score = normalizeScore(e.result)
  if (!score) return <p className="muted">The run produced no result.</p>

  return (
    <div className="panel panel-flush">
      <div className="band-pair">
        <span>
          Standing band <BandChip band={e.comparison.standingBand || undefined} />
        </span>
        <span aria-hidden="true">to</span>
        <span>
          Under this version <BandChip band={e.comparison.underThisVersion} />
        </span>
        {e.comparison.changes && <Status tone="warn">Band would change</Status>}
        {/* The API's note on how this run was made is a hover here rather
            than a callout under the steps: the steps are the answer. */}
        <ReasonTip label="About this run" text={e.note} />
      </div>

      <p className="muted">
        {tierLabel(score.inherentTier)} · control score {score.controlScore.toFixed(2)} · coverage{' '}
        {Math.round(score.coveragePct)}% · attested {Math.round(score.attestationPct)}% · matrix band{' '}
        {score.matrixBand}
      </p>
      <p className="muted">
        Against {e.against.reference || 'no assessment'}, {e.against.questions} question
        {e.against.questions === 1 ? '' : 's'} and {e.against.answers} answer
        {e.against.answers === 1 ? '' : 's'}.
      </p>

      <ol className="steps">
        {score.explanation.map((s, i) => (
          <li key={i}>
            <span className="step-name">{s.step}</span>
            <span className="step-detail">{s.detail}</span>
            {s.citation && <span className="step-cite">{s.citation}</span>}
          </li>
        ))}
      </ol>
    </div>
  )
}
